import React, { useState, useRef } from 'react';
import { 
  Languages, Upload, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Sparkles, Sliders, Edit3, Search, Scale, Briefcase, Wrench, MessageCircle, BookOpen, Layers
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import { LocalModelToggle } from './LocalModelToggle';

interface DocumentTranslatorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface TranslationPair {
  id: number;
  original: string;
  translated: string;
}

interface TranslationResult {
  documentTitle: string;
  detectedSourceLang: string;
  targetLang: string;
  styleApplied: string;
  summary: string;
  pairs: TranslationPair[];
}

export default function DocumentTranslator({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: DocumentTranslatorProps) {
  const isUk = language === 'uk';
  
  // Input state
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('en');
  const [translationStyle, setTranslationStyle] = useState('official-legal');
  const [customInstructions, setCustomInstructions] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [docxOrientation, setDocxOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  // Status & output state
  const [isExtracting, setIsExtracting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TranslationResult | null>(null);
  const [editedPairs, setEditedPairs] = useState<TranslationPair[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedText, setCopiedText] = useState(false);
  const [copiedBilingual, setCopiedBilingual] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Translation styles options
  const stylesOptions = [
    {
      id: 'official-legal',
      titleUk: 'Офіційно-діловий / Юридичний',
      titleEn: 'Official Legal & Business',
      descUk: 'Для договорів, угод, офіційних листів, позовів та нормативних актів.',
      descEn: 'For contracts, legal agreements, official letters, and compliance docs.',
      icon: Scale,
      color: 'border-purple-500 bg-purple-50/50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300'
    },
    {
      id: 'business',
      titleUk: 'Бізнес / Корпоративний',
      titleEn: 'Business & Executive',
      descUk: 'Для ділового листування, комерційних пропозицій, звітів та презентацій.',
      descEn: 'For executive correspondence, commercial proposals, and management reports.',
      icon: Briefcase,
      color: 'border-blue-500 bg-blue-50/50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-300'
    },
    {
      id: 'technical',
      titleUk: 'Технічний / Інженерний',
      titleEn: 'Technical & Engineering',
      descUk: 'Для специфікацій, інструкцій з експлуатації, патентів та стандартизацій.',
      descEn: 'For specifications, user manuals, equipment guides, and patents.',
      icon: Wrench,
      color: 'border-amber-500 bg-amber-50/50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-300'
    },
    {
      id: 'conversational',
      titleUk: 'Розмовний / Природний',
      titleEn: 'Conversational & Friendly',
      descUk: 'Для природного листування, електронної пошти та неформального спілкування.',
      descEn: 'For natural email communication and informal friendly exchanges.',
      icon: MessageCircle,
      color: 'border-emerald-500 bg-emerald-50/50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-300'
    },
    {
      id: 'literary',
      titleUk: 'Академічний / Науковий',
      titleEn: 'Academic & Research',
      descUk: 'Для аналітичних оглядів, наукових статей, есе та монографій.',
      descEn: 'For research papers, scientific articles, and comprehensive reviews.',
      icon: BookOpen,
      color: 'border-indigo-500 bg-indigo-50/50 text-indigo-700 dark:bg-indigo-950/20 dark:text-indigo-300'
    }
  ];

  // Languages options
  const langOptions = [
    { code: 'auto', nameUk: '🌐 Автовизначення', nameEn: '🌐 Auto-detect' },
    { code: 'uk', nameUk: '🇺🇦 Українська', nameEn: '🇺🇦 Ukrainian' },
    { code: 'en', nameUk: '🇬🇧 English', nameEn: '🇬🇧 English' },
    { code: 'de', nameUk: '🇩🇪 Deutsch', nameEn: '🇩🇪 German' },
    { code: 'pl', nameUk: '🇵🇱 Polski', nameEn: '🇵🇱 Polish' },
    { code: 'fr', nameUk: '🇫🇷 Français', nameEn: '🇫🇷 French' },
    { code: 'es', nameUk: '🇪🇸 Español', nameEn: '🇪🇸 Spanish' },
    { code: 'it', nameUk: '🇮🇹 Italiano', nameEn: '🇮🇹 Italian' },
    { code: 'ro', nameUk: '🇷🇴 Română', nameEn: '🇷🇴 Romanian' },
    { code: 'tr', nameUk: '🇹🇷 Türkçe', nameEn: '🇹🇷 Turkish' },
    { code: 'zh', nameUk: '🇨🇳 中文', nameEn: '🇨🇳 Chinese' }
  ];

  // Handle File Upload and Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize(file.size);
    setError(null);
    setIsExtracting(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка вилучення тексту з файлу.' : 'Failed to extract text from the file.'));
      }

      const data = await response.json();
      setText(data.text);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося прочитати файл.' : 'Failed to read file.'));
      setFileName(null);
      setFileSize(null);
    } finally {
      setIsExtracting(false);
    }
  };

  // Run Document Translation via Backend
  const handleTranslate = async () => {
    if (!text.trim()) return;

    setIsTranslating(true);
    setError(null);
    setResult(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/translate-document', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          sourceLang,
          targetLang,
          style: translationStyle,
          customInstructions,
          useEnterpriseContext,
          useLocalModel
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка перекладу документа.' : 'Failed to translate document.'));
      }

      const data: TranslationResult = await response.json();
      setResult(data);
      setEditedPairs(data.pairs || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Помилка при виконанні перекладу.' : 'An error occurred during translation.'));
    } finally {
      setIsTranslating(false);
    }
  };

  // Edit individual translated pair
  const handlePairChange = (index: number, newTranslatedText: string) => {
    const updated = [...editedPairs];
    updated[index].translated = newTranslatedText;
    setEditedPairs(updated);
  };

  // Download DOCX file
  const handleDownloadDocx = async () => {
    if (!result || editedPairs.length === 0) return;

    setIsDownloading(true);
    try {
      const sourceLangLabel = langOptions.find(l => l.code === sourceLang)?.nameUk || result.detectedSourceLang;
      const targetLangLabel = langOptions.find(l => l.code === targetLang)?.nameUk || result.targetLang;

      const response = await fetch('/api/generate-translated-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: result.documentTitle || (isUk ? 'Паралельний Переклад' : 'Parallel Translation'),
          sourceLangLabel: `ОРИГІНАЛ (${sourceLangLabel})`,
          targetLangLabel: `ПЕРЕКЛАД (${targetLangLabel} - ${result.styleApplied})`,
          pairs: editedPairs,
          orientation: docxOrientation
        })
      });

      if (!response.ok) {
        throw new Error(isUk ? 'Помилка завантаження Word-файлу.' : 'Failed to generate Word document.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTitle = (result.documentTitle || 'translated_document').replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, '_');
      a.download = `${safeTitle}_parallel_translation.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося згенерувати файл Word.' : 'Failed to export Word file.'));
    } finally {
      setIsDownloading(false);
    }
  };

  // Copy full translated text
  const handleCopyTranslatedOnly = () => {
    const fullText = editedPairs.map(p => p.translated).join('\n\n');
    navigator.clipboard.writeText(fullText);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2500);
  };

  // Copy bilingual text (side-by-side formatted)
  const handleCopyBilingualText = () => {
    const fullText = editedPairs.map((p, i) => `[#${i + 1} Original]\n${p.original}\n\n[#${i + 1} Translation]\n${p.translated}\n----------------------------------------`).join('\n\n');
    navigator.clipboard.writeText(fullText);
    setCopiedBilingual(true);
    setTimeout(() => setCopiedBilingual(false), 2500);
  };

  // Filter pairs by search term
  const filteredPairs = editedPairs.filter(
    p => p.original.toLowerCase().includes(searchTerm.toLowerCase()) || 
         p.translated.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button 
              onClick={onBack}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isUk ? "← До Офісного Центру" : "← Back to Office Hub"}</span>
            </button>
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400">
            <Languages className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                {isUk ? 'Перекладач Документів (Side-by-Side)' : 'Document Translator AI (Side-by-Side)'}
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800">
                ✨ Gemini 3.7 Flash
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isUk 
                ? 'Професійний паралельний переклад договорів, листів та документації з розбиттям абзац-в-абзац та експортом у Word.'
                : 'Professional side-by-side document translation for contracts and official letters with Word (.docx) export.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />

          {useEnterpriseContext && enterpriseName && (
            <div className="flex items-center gap-2 text-xs bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <span>{isUk ? `Контекст компанії: ${enterpriseName}` : `Company context: ${enterpriseName}`}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Input Form if no result yet */}
      {!result ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: File & Text Input (2 Spans) */}
          <div className="lg:col-span-2 space-y-5">
            {/* File Upload Box */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm">
              <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                {isUk ? '1. Завантажте файл (Word .docx, PDF, TXT)' : '1. Upload document (Word .docx, PDF, TXT)'}
              </label>
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  isExtracting 
                    ? 'border-purple-400 bg-purple-50/30 dark:bg-purple-950/10' 
                    : fileName 
                    ? 'border-emerald-400 bg-emerald-50/30 dark:bg-emerald-950/10' 
                    : 'border-slate-300 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileUpload} 
                  accept=".pdf,.docx,.doc,.txt" 
                  className="hidden" 
                />

                {isExtracting ? (
                  <div className="flex flex-col items-center py-2">
                    <RefreshCw className="w-8 h-8 text-purple-600 animate-spin mb-2" />
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {isUk ? 'Зчитування та вилучення тексту з файлу...' : 'Extracting text from file...'}
                    </p>
                  </div>
                ) : fileName ? (
                  <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 text-left">
                      <FileText className="w-8 h-8 text-purple-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-xs">{fileName}</p>
                        <p className="text-xs text-slate-500">{fileSize ? `${(fileSize / 1024).toFixed(1)} KB` : ''}</p>
                      </div>
                    </div>
                    <span className="text-xs text-purple-600 font-semibold hover:underline">
                      {isUk ? 'Змінити файл' : 'Change file'}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {isUk ? 'Натисніть для вибору або перетягніть файл сюди' : 'Click to select or drag and drop a file'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DOCX, DOC, TXT (до 25 МБ)</p>
                  </div>
                )}
              </div>

              {/* Quick Demo Sample Button */}
              <button
                type="button"
                onClick={() => {
                  setFileName("sample_technical_contract.docx");
                  setFileSize(16500);
                  setTargetLang("uk");
                  setText(`COMMERCIAL SUPPLY AGREEMENT
This Supply Agreement is made and entered into on January 12, 2026, by and between:
1. Vendor: Global Energy Systems Ltd, registered under the laws of Germany.
2. Purchaser: State Enterprise HydroTech Ukraine.

1. SCOPE OF SERVICES
1.1. The Vendor shall manufacture, inspect, deliver, and install industrial water filtration systems according to Technical Specifications Annex A.
1.2. All equipment supplied under this Agreement shall carry a 36-month full warranty.

2. PENALTIES AND LIABILITY
2.1. In the event of delay in delivery exceeding 10 business days, the Vendor shall pay a penalty of 0.2% per day of the total order value.
2.2. Total liquidated damages shall not exceed 10% of the Agreement price.`);
                  setError(null);
                }}
                className="w-full mt-3 py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-900/40 border border-emerald-300 dark:border-emerald-700 rounded-xl text-emerald-800 dark:text-emerald-300 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
              >
                <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400 animate-pulse" />
                <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Паралельний переклад EN ➔ UK)" : "⚡ Try 1-click Demo Sample (Parallel Translation EN ➔ UK)"}</span>
              </button>
            </div>

            {/* Textarea Input */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-900 dark:text-white">
                  {isUk ? '2. Або введіть / відредагуйте текст документа' : '2. Or paste / edit document text'}
                </label>
                {text && (
                  <button 
                    onClick={() => { setText(''); setFileName(null); }}
                    className="text-xs text-rose-500 hover:underline"
                  >
                    {isUk ? 'Очистити текст' : 'Clear text'}
                  </button>
                )}
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isUk 
                  ? 'Вставить текст офіційного листа, договору, специфікації або угоди сюди...' 
                  : 'Paste the text of an official letter, contract, specification, or legal agreement here...'}
                className="w-full h-64 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none font-mono"
              />

              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>
                  {isUk 
                    ? `Символів: ${text.length.toLocaleString()} | Слів: ${text.trim() ? text.trim().split(/\s+/).length : 0}`
                    : `Characters: ${text.length.toLocaleString()} | Words: ${text.trim() ? text.trim().split(/\s+/).length : 0}`}
                </span>
                <span>{isUk ? 'Підтримуються будь-які мови' : 'Supports all languages'}</span>
              </div>
            </div>
          </div>

          {/* Right Column: Translation Parameters & Style Selection */}
          <div className="space-y-5">
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Sliders className="w-4 h-4 text-purple-600" />
                {isUk ? 'Параметри перекладу' : 'Translation Options'}
              </h3>

              {/* Language Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    {isUk ? 'Мова оригинала' : 'Source Language'}
                  </label>
                  <select
                    value={sourceLang}
                    onChange={(e) => setSourceLang(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-purple-500"
                  >
                    {langOptions.map(l => (
                      <option key={l.code} value={l.code}>
                        {isUk ? l.nameUk : l.nameEn}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    {isUk ? 'Мова перекладу' : 'Target Language'}
                  </label>
                  <select
                    value={targetLang}
                    onChange={(e) => setTargetLang(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs font-bold focus:ring-2 focus:ring-purple-500"
                  >
                    {langOptions.filter(l => l.code !== 'auto').map(l => (
                      <option key={l.code} value={l.code}>
                        {isUk ? l.nameUk : l.nameEn}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Translation Styles Selector */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                  {isUk ? 'Стиль перекладу' : 'Translation Style'}
                </label>
                <div className="space-y-2">
                  {stylesOptions.map((st) => {
                    const IconComp = st.icon;
                    const isSelected = translationStyle === st.id;
                    return (
                      <div
                        key={st.id}
                        onClick={() => setTranslationStyle(st.id)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                          isSelected 
                            ? st.color + ' ring-2 ring-purple-500/20' 
                            : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <IconComp className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isSelected ? 'text-purple-600' : 'text-slate-400'}`} />
                        <div>
                          <p className="text-xs font-bold">{isUk ? st.titleUk : st.titleEn}</p>
                          <p className="text-[11px] opacity-80 mt-0.5 leading-relaxed">
                            {isUk ? st.descUk : st.descEn}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Collapsible Advanced Glossaries / Instructions */}
              <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
                >
                  <span>{showAdvanced ? '▼' : '►'}</span>
                  <span>{isUk ? 'Спеціальні вказівки та Глосарій (опціонально)' : 'Custom Glossary & Rules (optional)'}</span>
                </button>

                {showAdvanced && (
                  <div className="mt-2">
                    <textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder={isUk 
                        ? 'Вкажіть специфічні терміни, вимоги до перекладу назв компаній або власні назви (наприклад: "TechCorp" не перекладати, "Escrow" = "Ескроу-рахунок")...' 
                        : 'Specify terminology rules or untranslatable terms...'}
                      className="w-full h-24 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white text-xs focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Action Button */}
              <button
                onClick={handleTranslate}
                disabled={!text.trim() || isTranslating}
                className={`w-full py-3.5 px-4 rounded-xl font-bold text-sm text-white shadow-lg shadow-purple-500/20 transition-all flex items-center justify-center gap-2 ${
                  !text.trim() || isTranslating
                    ? 'bg-slate-400 cursor-not-allowed shadow-none'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:scale-[0.98]'
                }`}
              >
                {isTranslating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>{isUk ? 'Виконується паралельний переклад...' : 'Translating Side-by-Side...'}</span>
                  </>
                ) : (
                  <>
                    <Languages className="w-5 h-5" />
                    <span>{isUk ? 'Перекласти документ (Side-by-Side)' : 'Translate Document (Side-by-Side)'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Results View (Side-by-Side Table & Export Panel) */
        <div className="space-y-6">
          {/* Document Summary Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  {result.documentTitle || (isUk ? 'Паралельний Переклад Документа' : 'Document Parallel Translation')}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                  {result.styleApplied}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {isUk 
                  ? `Мова оригіналу: ${result.detectedSourceLang} | Мова перекладу: ${result.targetLang} | Абзаців: ${result.pairs.length}`
                  : `Original: ${result.detectedSourceLang} | Target: ${result.targetLang} | Paragraphs: ${result.pairs.length}`}
              </p>
              {result.summary && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 bg-slate-50 dark:bg-slate-800/60 p-2.5 rounded-lg border border-slate-200/60 dark:border-slate-700/60 italic">
                  "{result.summary}"
                </p>
              )}
            </div>

            {/* Top Toolbar Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Word Page Orientation Selector */}
              <select
                value={docxOrientation}
                onChange={(e) => setDocxOrientation(e.target.value as 'portrait' | 'landscape')}
                className="px-2.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-xs font-semibold focus:ring-2 focus:ring-purple-500"
                title={isUk ? "Орієнтація сторінки Word" : "Word page orientation"}
              >
                <option value="portrait">{isUk ? '📄 Книжкова (50%/50% аркуша)' : '📄 Portrait (50%/50%)'}</option>
                <option value="landscape">{isUk ? '📑 Альбомна (50%/50% широка)' : '📑 Landscape (50%/50% wide)'}</option>
              </select>

              <TelegramExportButton
                title={result.documentTitle || 'Переклад документа'}
                content={editedPairs.map((p, i) => `## Абзац ${i + 1}\n**Оригінал:** ${p.original}\n\n**Переклад:** ${p.translated}\n`).join('\n---\n\n')}
                agentName="ШІ-Перекладач Документів (Side-by-Side)"
                agentId="doc-translator"
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={handleDownloadDocx}
                disabled={isDownloading}
                className="px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-md shadow-purple-500/20 flex items-center gap-2 transition-all active:scale-[0.98]"
              >
                {isDownloading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isUk ? 'Завантажити у Word (.docx)' : 'Download Word (.docx)'}</span>
              </button>

              <button
                onClick={handleCopyTranslatedOnly}
                className="px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {copiedText ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                <span>{copiedText ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Скопійовати переклад' : 'Copy Translation')}</span>
              </button>

              <button
                onClick={handleCopyBilingualText}
                className="px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {copiedBilingual ? <Check className="w-4 h-4 text-emerald-500" /> : <Layers className="w-4 h-4" />}
                <span>{copiedBilingual ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Копіювати двомовний' : 'Copy Bilingual')}</span>
              </button>

              <button
                onClick={() => setResult(null)}
                className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-medium transition-colors"
              >
                {isUk ? 'Новий переклад' : 'New Translation'}
              </button>
            </div>
          </div>

          {/* Search bar inside results */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={isUk ? 'Пошук за текстом оригіналу чи перекладу...' : 'Search within original or translation...'}
                className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <p className="text-xs text-slate-500">
              {isUk ? '💡 Порада: Ви можете редагувати переклад прямо в таблиці перед завантаженням Word!' : '💡 Tip: You can edit translation directly in the table before downloading Word!'}
            </p>
          </div>

          {/* Side-by-Side Parallel Table Preview */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold uppercase tracking-wider p-3.5 divide-y md:divide-y-0 md:divide-x divide-purple-500/30">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>{isUk ? `Оригінальний текст (${result.detectedSourceLang})` : `Original Text (${result.detectedSourceLang})`}</span>
              </div>
              <div className="flex items-center gap-2 pt-2 md:pt-0">
                <Languages className="w-4 h-4" />
                <span>{isUk ? `Переклад (${result.targetLang} - ${result.styleApplied})` : `Translation (${result.targetLang} - ${result.styleApplied})`}</span>
              </div>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[700px] overflow-y-auto">
              {filteredPairs.map((pair, idx) => (
                <div key={pair.id || idx} className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800 hover:bg-purple-50/20 dark:hover:bg-purple-950/10 transition-colors">
                  {/* Left Column: Original */}
                  <div className="p-4 space-y-1">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 mb-1">
                      #{pair.id || idx + 1}
                    </span>
                    <p className="text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed font-sans">
                      {pair.original}
                    </p>
                  </div>

                  {/* Right Column: Translation (Editable) */}
                  <div className="p-4 space-y-1 bg-slate-50/30 dark:bg-slate-900/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300">
                        #{pair.id || idx + 1} Translation
                      </span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Edit3 className="w-3 h-3" />
                        {isUk ? 'Редагувати' : 'Edit'}
                      </span>
                    </div>
                    <textarea
                      value={pair.translated}
                      onChange={(e) => handlePairChange(idx, e.target.value)}
                      rows={Math.max(2, pair.translated.split('\n').length)}
                      className="w-full p-2.5 rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none resize-y font-sans leading-relaxed"
                    />
                  </div>
                </div>
              ))}

              {filteredPairs.length === 0 && (
                <div className="p-8 text-center text-slate-500 text-xs">
                  {isUk ? 'Абзаців за вашим пошуком не знайдено.' : 'No paragraphs match your search.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
